import React from 'react';
import type { Editor } from '@tiptap/core';
// Side-effect imports only: these extensions augment @tiptap/core's Commands
// interface (toggleUnderline/toggleHighlight/toggleTaskList) and are registered
// on the editor by the full editor shell, not by this module.
import '@tiptap/extension-underline';
import '@tiptap/extension-highlight';
import '@tiptap/extension-task-list';
import {
  Bold,
  Italic,
  Strikethrough,
  Underline as UnderlineIcon,
  Code,
  CodeXml,
  Highlighter,
  Heading,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  TextQuote,
  List,
  ListOrdered,
  ListTodo
} from 'lucide-react';
import {
  toggleWrap,
  toggleLinePrefix,
  toggleOrderedList,
  toggleCodeBlock,
  setHeadingLevel,
  type MarkdownFormatResult
} from '../utils/markdownFormatting';
import { LANGUAGE_OPTIONS, ensureLanguageLoaded } from '../extensions/codeLanguages';
import { sharedLowlight } from '../extensions/tiptapShared';

const I = 14;
const Is = 13;

export type MarkdownAction = (text: string, start: number, end: number) => MarkdownFormatResult;

export type DropdownOption = {
  id: string;
  label: string;
  icon?: React.ReactNode;
  shortcut?: string[];
  isActive: (editor: Editor) => boolean;
  onSelect: (editor: Editor, close: () => void) => void;
  markdownAction?: MarkdownAction;
};

export type DropdownDef = {
  id: string;
  getLabel: (editor: Editor) => React.ReactNode;
  title: string;
  isActive: (editor: Editor) => boolean;
  options: DropdownOption[];
};

export type ButtonDef = {
  id: string;
  icon: React.ReactNode;
  title: string;
  shortcut?: string[];
  isActive: (editor: Editor) => boolean;
  onClick: (editor: Editor) => void;
  markdownAction?: MarkdownAction;
};

export type ToolbarItemDef =
  | { type: 'button'; def: ButtonDef }
  | { type: 'dropdown'; def: DropdownDef };

export function getToolbarItems(editor: Editor): ToolbarItemDef[] {
  const chain = () => editor.chain().focus();

  return [
    { type: 'button', def: { id: 'bold', icon: <Bold size={I} />, title: 'Bold', shortcut: ['mod', 'B'], isActive: e => e.isActive('bold'), onClick: () => chain().toggleBold().run(), markdownAction: (t, s, e) => toggleWrap(t, s, e, '**') } },
    { type: 'button', def: { id: 'italic', icon: <Italic size={I} />, title: 'Italic', shortcut: ['mod', 'I'], isActive: e => e.isActive('italic'), onClick: () => chain().toggleItalic().run(), markdownAction: (t, s, e) => toggleWrap(t, s, e, '*') } },
    { type: 'button', def: { id: 'underline', icon: <UnderlineIcon size={I} />, title: 'Underline', shortcut: ['mod', 'U'], isActive: e => e.isActive('underline'), onClick: () => chain().toggleUnderline().run(), markdownAction: (t, s, e) => toggleWrap(t, s, e, '++') } },
    { type: 'button', def: { id: 'strike', icon: <Strikethrough size={I} />, title: 'Strikethrough', shortcut: ['mod', 'shift', 'S'], isActive: e => e.isActive('strike'), onClick: () => chain().toggleStrike().run(), markdownAction: (t, s, e) => toggleWrap(t, s, e, '~~') } },
    { type: 'button', def: { id: 'highlight', icon: <Highlighter size={I} />, title: 'Highlight', shortcut: ['mod', 'shift', 'H'], isActive: e => e.isActive('highlight'), onClick: () => chain().toggleHighlight().run(), markdownAction: (t, s, e) => toggleWrap(t, s, e, '==') } },
    { type: 'button', def: { id: 'quote', icon: <TextQuote size={I} />, title: 'Quote', shortcut: ['mod', 'shift', 'B'], isActive: e => e.isActive('blockquote'), onClick: () => chain().toggleBlockquote().run(), markdownAction: (t, s, e) => toggleLinePrefix(t, s, e, '> ') } },
    {
      type: 'dropdown',
      def: {
        id: 'code',
        getLabel: e => (e.isActive('codeBlock') ? <CodeXml size={I} /> : <Code size={I} />),
        title: 'Code',
        isActive: e => e.isActive('code') || e.isActive('codeBlock'),
        options: editor.isActive('codeBlock')
          ? [
              {
                id: 'to-text',
                label: 'Convert to text',
                icon: <CodeXml size={Is} />,
                isActive: () => false,
                onSelect: (e, close) => { e.chain().focus().toggleCodeBlock().run(); close(); },
                markdownAction: (t, s, e) => toggleCodeBlock(t, s, e)
              },
              ...LANGUAGE_OPTIONS.map(opt => ({
                id: `lang-${opt.id}`,
                label: opt.label,
                isActive: (e: Editor) => (e.getAttributes('codeBlock').language ?? 'plaintext') === opt.id,
                onSelect: (e: Editor, close: () => void) => {
                  close();
                  ensureLanguageLoaded(sharedLowlight, opt.id).then(() => {
                    e.chain().focus().updateAttributes('codeBlock', { language: opt.id }).run();
                  });
                }
              }))
            ]
          : [
              { id: 'inline', label: 'Inline code', icon: <Code size={Is} />, shortcut: ['mod', 'E'], isActive: e => e.isActive('code'), onSelect: (e, close) => { e.chain().focus().toggleCode().run(); close(); }, markdownAction: (t, s, e) => toggleWrap(t, s, e, '`') },
              { id: 'block', label: 'Code block', icon: <CodeXml size={Is} />, shortcut: ['mod', 'alt', 'C'], isActive: e => e.isActive('codeBlock'), onSelect: (e, close) => { e.chain().focus().toggleCodeBlock().run(); close(); }, markdownAction: (t, s, e) => toggleCodeBlock(t, s, e) }
            ]
      }
    },
    {
      type: 'dropdown',
      def: {
        id: 'heading',
        getLabel: e => {
          const level = e.isActive('heading') ? e.getAttributes('heading').level : null;
          const headings: Record<number, React.ReactNode> = { 1: <Heading1 size={I} />, 2: <Heading2 size={I} />, 3: <Heading3 size={I} />, 4: <Heading4 size={I} /> };
          return level && level in headings ? headings[level] : <Heading size={I} />;
        },
        title: 'Headings',
        isActive: e => e.isActive('heading'),
        options: [
          ...([1, 2, 3, 4] as const).map(level => ({
            id: `h${level}`,
            label: `Heading ${level}`,
            shortcut: ['mod', 'alt', String(level)],
            isActive: (e: Editor) => e.isActive('heading', { level }),
            onSelect: (e: Editor, close: () => void) => {
              e.chain().focus().toggleHeading({ level }).run();
              close();
            },
            markdownAction: (t: string, s: number, e: number) => setHeadingLevel(t, s, e, level)
          })),
          {
            id: 'paragraph',
            label: 'Paragraph',
            shortcut: ['mod', 'alt', '0'],
            isActive: (e: Editor) => !e.isActive('heading'),
            onSelect: (e: Editor, close: () => void) => {
              e.chain().focus().setParagraph().run();
              close();
            },
            markdownAction: (t: string, s: number, e: number) => setHeadingLevel(t, s, e, null)
          }
        ]
      }
    },
    {
      type: 'dropdown',
      def: {
        id: 'list',
        getLabel: e =>
          e.isActive('taskList') ? <ListTodo size={I} /> : e.isActive('orderedList') ? <ListOrdered size={I} /> : <List size={I} />,
        title: 'Lists',
        isActive: e => e.isActive('bulletList') || e.isActive('orderedList') || e.isActive('taskList'),
        options: [
          { id: 'bullet', label: 'Bullet list', icon: <List size={Is} />, shortcut: ['mod', 'shift', '8'], isActive: e => e.isActive('bulletList'), onSelect: (e, close) => { e.chain().focus().toggleBulletList().run(); close(); }, markdownAction: (t, s, e) => toggleLinePrefix(t, s, e, '- ') },
          { id: 'ordered', label: 'Numbered list', icon: <ListOrdered size={Is} />, shortcut: ['mod', 'shift', '7'], isActive: e => e.isActive('orderedList'), onSelect: (e, close) => { e.chain().focus().toggleOrderedList().run(); close(); }, markdownAction: (t, s, e) => toggleOrderedList(t, s, e) },
          { id: 'task', label: 'Checkbox list', icon: <ListTodo size={Is} />, shortcut: ['mod', 'shift', '9'], isActive: e => e.isActive('taskList'), onSelect: (e, close) => { e.chain().focus().toggleTaskList().run(); close(); }, markdownAction: (t, s, e) => toggleLinePrefix(t, s, e, '- [ ] ') }
        ]
      }
    }
  ];
}
